import axios, { AxiosInstance, AxiosResponse } from 'axios'

// Get API URL from environment - should point to /api/v2
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v2'
// For non-v2 endpoints (auth, media, etc.)
const API_BASE_URL_V1 = API_BASE_URL.replace('/v2', '')

// Global flag to prevent multiple simultaneous logout calls across all instances
// This is important in production where multiple instances might exist
let globalIsLoggingOut = false
let logoutEventDispatched = false

class ApiService {
  private api: AxiosInstance
  private apiV1: AxiosInstance // For non-v2 endpoints
  private isRefreshing = false
  private isLoggingOut = false // Flag to prevent multiple simultaneous logout calls
  private failedQueue: Array<{
    resolve: (value?: any) => void
    reject: (error?: any) => void
  }> = []

  constructor() {
    // V2 API instance (main)
    this.api = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // V1 API instance (for auth, media endpoints)
    this.apiV1 = axios.create({
      baseURL: API_BASE_URL_V1,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  private setupInterceptors() {
    // Request interceptor for V2 API
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Request interceptor for V1 API
    this.apiV1.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    // Response interceptor for V2 API
    this.api.interceptors.response.use(
      (response: AxiosResponse) => {
        return response
      },
      (error) => {
        return this.handleError(error)
      }
    )

    // Response interceptor for V1 API
    this.apiV1.interceptors.response.use(
      (response: AxiosResponse) => {
        return response
      },
      (error) => {
        return this.handleError(error)
      }
    )
  }

  private async handleError(error: any) {
    const status = error.response?.status
    const data = error.response?.data
    // Extract error message: backend sends { error: { message: "..." } } for 4xx/5xx
    const errorMessage = 
      (typeof data?.error === 'object' ? data?.error?.message : data?.error) ||
      data?.message ||
      data?.details ||
      error.message ||
      ''
    // Attach backend message to error so catch blocks can use error.message
    if (errorMessage && typeof errorMessage === 'string') {
      error.message = errorMessage
    }
    const requestUrl = error.config?.url || ''
    
    // For 429 (Rate Limit) errors, enhance the error message
    if (status === 429) {
      const retryAfter = error.response?.data?.retryAfter || error.response?.headers?.['retry-after']
      const enhancedMessage = retryAfter 
        ? `Too many requests. Please try again in ${retryAfter} seconds.`
        : errorMessage || 'Too many requests. Please try again later.'
      
      // Update the error object with the enhanced message
      error.response = error.response || {}
      error.response.data = error.response.data || {}
      error.response.data.message = enhancedMessage
      error.response.data.error = enhancedMessage
    }
    
    console.log('[LOGOUT-DEBUG] handleError called', {
      status,
      url: requestUrl,
      message: errorMessage,
      isLoggingOut: this.isLoggingOut,
      globalIsLoggingOut,
      isRefreshing: this.isRefreshing,
      timestamp: new Date().toISOString()
    })
    
    // Don't trigger logout for auth endpoint errors (login/logout/register/verify/etc.)
    // - 401 on login is expected for wrong credentials
    // - 401 on logout is expected (token might already be invalid/cleared)
    // - Errors on register/verify endpoints should be handled locally, not trigger logout
    const isLoginEndpoint = requestUrl.includes('/auth/login')
    const isLogoutEndpoint = requestUrl.includes('/auth/logout')
    const isRegisterEndpoint = requestUrl.includes('/auth/register')
    const isVerifyEndpoint = requestUrl.includes('/auth/verify')
    const isRefreshEndpoint = requestUrl.includes('/auth/refresh')
    const isPublicAuthEndpoint = isLoginEndpoint || isLogoutEndpoint || isRegisterEndpoint || isVerifyEndpoint || isRefreshEndpoint
    
    // Also check global flag - if already logging out globally, don't process errors
    if (globalIsLoggingOut) {
      console.log('[LOGOUT-DEBUG] Skipping error handling - already logging out globally')
      return Promise.reject(error)
    }
    
    // Ensure errorMessage is a string before calling toLowerCase
    const errorMessageStr = typeof errorMessage === 'string' ? errorMessage : String(errorMessage || '')
    
    const isAuthError = 
      status === 401 || 
      status === 403 || 
      errorMessageStr.toLowerCase().includes('access token') ||
      errorMessageStr.toLowerCase().includes('token required') ||
      errorMessageStr.toLowerCase().includes('unauthorized') ||
      errorMessageStr.toLowerCase().includes('authentication')

    // Skip logout handling for public auth endpoints - errors are expected and handled locally
    if (isAuthError && isPublicAuthEndpoint) {
      console.log('[LOGOUT-DEBUG] Skipping logout - public auth endpoint error (expected)', {
        isLoginEndpoint,
        isLogoutEndpoint,
        isRegisterEndpoint,
        isVerifyEndpoint,
        isRefreshEndpoint
      })
      return Promise.reject(error)
    }

    if (isAuthError && status === 401) {
      // Try to refresh token before redirecting
      const refreshToken = localStorage.getItem('refreshToken')
      
      console.log('[LOGOUT-DEBUG] 401 error detected, checking refresh token', {
        hasRefreshToken: !!refreshToken,
        isRefreshing: this.isRefreshing
      })
      
      if (refreshToken && !this.isRefreshing) {
        console.log('[LOGOUT-DEBUG] Attempting token refresh')
        return this.handleTokenRefresh(error)
      }
      
      // If already refreshing, queue this request
      if (this.isRefreshing) {
        console.log('[LOGOUT-DEBUG] Already refreshing, queueing request')
        return new Promise((resolve, reject) => {
          this.failedQueue.push({ resolve, reject })
        }).then(() => {
          // Retry the original request
          const config = error.config
          const token = localStorage.getItem('authToken')
          if (token && config) {
            config.headers.Authorization = `Bearer ${token}`
            return axios(config)
          }
          return Promise.reject(error)
        }).catch((err) => {
          return Promise.reject(err)
        })
      }
    }

    // For 403 or other auth errors, or if refresh failed, logout
    // But only if not already logging out
    if (isAuthError && !this.isLoggingOut) {
      console.log('[LOGOUT-DEBUG] Auth error detected, calling logoutUser()', {
        status,
        isAuthError,
        isLoggingOut: this.isLoggingOut
      })
      this.logoutUser()
    } else if (isAuthError) {
      console.log('[LOGOUT-DEBUG] Auth error but already logging out, skipping', {
        status,
        isLoggingOut: this.isLoggingOut
      })
    }
    
    return Promise.reject(error)
  }

  private async handleTokenRefresh(originalError: any) {
    this.isRefreshing = true
    
    try {
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) {
        throw new Error('No refresh token available')
      }

      // Try to refresh the token
      const response = await axios.post(
        `${API_BASE_URL_V1}/auth/refresh`,
        { refreshToken },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (response.data.success && response.data.data?.tokens?.accessToken) {
        const { accessToken, refreshToken: newRefreshToken } = response.data.data.tokens
        
        // Update tokens in localStorage
        localStorage.setItem('authToken', accessToken)
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken)
        }

        // Update the original request with new token
        const config = originalError.config
        if (config) {
          config.headers.Authorization = `Bearer ${accessToken}`
          
          // Process queued requests
          this.processQueue(null)
          
          // Retry the original request
          return axios(config)
        }
      }
      
      throw new Error('Token refresh failed')
    } catch (refreshError) {
      // Refresh failed, process queue with error and logout
      console.log('[LOGOUT-DEBUG] Token refresh FAILED, calling logoutUser()', {
        error: refreshError,
        timestamp: new Date().toISOString()
      })
      this.processQueue(refreshError)
      this.logoutUser()
      return Promise.reject(originalError)
    } finally {
      this.isRefreshing = false
    }
  }

  private processQueue(error: any) {
    this.failedQueue.forEach((promise) => {
      if (error) {
        promise.reject(error)
      } else {
        promise.resolve()
      }
    })
    this.failedQueue = []
  }

  private logoutUser() {
    console.log('[LOGOUT-DEBUG] logoutUser() called', {
      isLoggingOut: this.isLoggingOut,
      globalIsLoggingOut,
      logoutEventDispatched,
      timestamp: new Date().toISOString(),
      stackTrace: new Error().stack?.split('\n').slice(1, 4).join('\n')
    })
    
    // Prevent multiple simultaneous logout calls (both instance and global level)
    if (this.isLoggingOut || globalIsLoggingOut) {
      console.log('[LOGOUT-DEBUG] logoutUser() BLOCKED - already logging out', {
        isLoggingOut: this.isLoggingOut,
        globalIsLoggingOut
      })
      return
    }
    
    // Set flags immediately to prevent race conditions
    this.isLoggingOut = true
    globalIsLoggingOut = true
    console.log('[LOGOUT-DEBUG] Flags set - starting logout process')
    
    // Clear all auth data FIRST (before any async operations)
    localStorage.removeItem('authToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')
    localStorage.removeItem('schools')
    localStorage.removeItem('selectedSchool')
    console.log('[LOGOUT-DEBUG] LocalStorage cleared')
    
    // Dispatch custom event to trigger logout in AuthContext (only once globally)
    if (!logoutEventDispatched) {
      logoutEventDispatched = true
      console.log('[LOGOUT-DEBUG] Dispatching auth:logout event')
      window.dispatchEvent(new CustomEvent('auth:logout'))
    } else {
      console.log('[LOGOUT-DEBUG] Event already dispatched, skipping')
    }
    
    // Redirect to login page
    if (window.location.pathname !== '/login') {
      console.log('[LOGOUT-DEBUG] Redirecting to /login')
      window.location.href = '/login'
    } else {
      console.log('[LOGOUT-DEBUG] Already on /login, skipping redirect')
    }
    
    // Reset flags after a delay to allow redirect
    setTimeout(() => {
      console.log('[LOGOUT-DEBUG] Resetting flags after timeout')
      this.isLoggingOut = false
      globalIsLoggingOut = false
      logoutEventDispatched = false
    }, 3000)
  }

  // Auth endpoints (common /api/auth - shared across all portals)
  async login(email: string, password: string) {
    const response = await this.apiV1.post('/auth/login', { 
      email, 
      password,
      portal: 'school' // Specify this is the yoga school admin portal
    })
    return response.data
  }

  async registerYogaSchoolOwner(data: {
    firstName: string
    lastName: string
    email: string
    password: string
    phone?: string
    acceptedTerms: boolean
  }) {
    const response = await this.apiV1.post('/auth/register/yoga-school-owner', data)
    return response.data
  }

  async logout() {
    console.log('[LOGOUT-DEBUG] logout() API method called', {
      timestamp: new Date().toISOString(),
      hasToken: !!localStorage.getItem('authToken')
    })
    
    // Silently handle logout - don't throw errors if logout fails
    try {
      const token = localStorage.getItem('authToken')
      console.log('[LOGOUT-DEBUG] Making logout API call', {
        url: `${API_BASE_URL_V1}/auth/logout`,
        hasToken: !!token
      })
      
      const response = await axios.post(
        `${API_BASE_URL_V1}/auth/logout`,
        {},
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      )
      
      console.log('[LOGOUT-DEBUG] Logout API call SUCCESS', {
        status: response.status,
        data: response.data
      })
    } catch (error: any) {
      // Log the error for debugging
      console.log('[LOGOUT-DEBUG] Logout API call FAILED (expected if token invalid)', {
        status: error.response?.status,
        message: error.message,
        url: error.config?.url,
        timestamp: new Date().toISOString()
      })
    }
  }

  async refreshToken() {
    const refreshToken = localStorage.getItem('refreshToken')
    if (!refreshToken) {
      throw new Error('No refresh token available')
    }
    const response = await this.apiV1.post('/auth/refresh', { refreshToken })
    return response.data
  }

  async verifyEmail(email: string, code: string) {
    const response = await this.apiV1.post('/auth/verify-email', { email, code })
    return response.data
  }

  async resendVerificationEmail(email: string) {
    const response = await this.apiV1.post('/auth/resend-verification', { email })
    return response.data
  }

  // School endpoints (V2)
  async createSchool(data: any) {
    const response = await this.api.post('/schools', data)
    return response.data
  }

  async getSchoolProfile() {
    const response = await this.api.get('/schools/profile')
    return response.data
  }

  async updateSchoolProfile(data: any) {
    const response = await this.api.put('/schools/profile', data)
    return response.data
  }

  async claimSchool(schoolId: string) {
    const response = await this.api.post('/schools/claim', { schoolId })
    return response.data
  }

  async uploadSchoolImage(file: File, category: string) {
    const formData = new FormData()
    formData.append('image', file)
    formData.append('category', category)

    const response = await this.api.post('/schools/upload-image', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  async uploadSchoolVideo(file: File, category: string) {
    const formData = new FormData()
    formData.append('video', file)
    formData.append('category', category)

    const response = await this.api.post('/schools/upload-video', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    })
    return response.data
  }

  // Course endpoints (V2)
  async getCourses(params?: any) {
    const response = await this.api.get('/courses/my-school', { params })
    return response.data
  }

  async getCourse(id: string) {
    const response = await this.api.get(`/courses/${id}`)
    return response.data
  }

  async createCourse(data: any) {
    const response = await this.api.post('/courses', data)
    return response.data
  }

  async updateCourse(id: string, data: any) {
    const response = await this.api.put(`/courses/${id}`, data)
    return response.data
  }

  async deleteCourse(id: string) {
    const response = await this.api.delete(`/courses/${id}`)
    return response.data
  }

  async toggleCourseStatus(id: string) {
    const response = await this.api.patch(`/courses/${id}/status`)
    return response.data
  }

  async toggleCourseFeatured(id: string) {
    const response = await this.api.patch(`/courses/${id}/feature`)
    return response.data
  }

  // Booking endpoints (V2)
  async getBookings(params?: any) {
    const response = await this.api.post('/bookings/search', params || {})
    return response.data
  }

  async getBooking(id: string) {
    const response = await this.api.get(`/bookings/${id}`)
    return response.data
  }

  async approveBooking(id: string, notes?: string) {
    const response = await this.api.post(`/bookings/${id}/approve`, { notes })
    return response.data
  }

  async rejectBooking(id: string, notes?: string) {
    const response = await this.api.post(`/bookings/${id}/reject`, { notes })
    return response.data
  }

  async updateBooking(id: string, data: any) {
    const response = await this.api.put(`/bookings/${id}`, data)
    return response.data
  }

  // Media endpoints (use V1 API - not v2, media routes are at /api/media)
  async getMediaFiles(params?: any) {
    const response = await this.apiV1.get('/media', { params })
    return response.data
  }

  async deleteMediaFile(id: string) {
    const response = await this.apiV1.delete(`/media/${id}`)
    return response.data
  }

  async updateMediaFile(id: string, data: any) {
    const response = await this.apiV1.put(`/media/${id}`, data)
    return response.data
  }

  // Media upload/signed URL endpoints (use V1 API - not v2)
  async getMediaUploadUrl(data: any) {
    const response = await this.apiV1.post('/media/upload-url', data)
    return response.data
  }

  async getMediaUrl(params: any) {
    const response = await this.apiV1.get('/media/url', { params })
    return response.data
  }

  async getBatchMediaUrls(data: any) {
    const response = await this.apiV1.post('/media/batch-urls', data)
    return response.data
  }

  // Dashboard endpoints (V2)
  async getDashboardStats() {
    const response = await this.api.get('/schools/dashboard-stats')
    return response.data
  }

  // User profile endpoints (common /api/auth)
  async getUserProfile() {
    const response = await this.apiV1.get('/auth/me')
    return response.data
  }

  async updateUserProfile(data: any) {
    const response = await this.apiV1.put('/auth/me', data)
    return response.data
  }

  async changePassword(currentPassword: string, newPassword: string) {
    const response = await this.apiV1.put('/users/change-password', {
      currentPassword,
      newPassword,
    })
    return response.data
  }

  async forgotPassword(email: string) {
    const response = await this.apiV1.post('/auth/forgot-password', { email })
    return response.data
  }

  async verifyOTP(email: string, otp: string) {
    const response = await this.apiV1.post('/auth/verify-otp', { email, otp })
    return response.data
  }

  async resetPassword(email: string, otp: string, password: string) {
    const response = await this.apiV1.post('/auth/reset-password', { email, otp, password })
    return response.data
  }

  /**
   * Claim profile flow pre-check:
   * - CLAIMABLE => continue with claim OTP
   * - REGISTER => send user to register
   * - VERIFY_EMAIL => send user to verify-email
   * - LOGIN => send user to login
   */
  async claimProfileCheck(email: string) {
    const response = await this.apiV1.post('/auth/claim-profile/check', { email })
    return response.data
  }

  /**
   * Claim profile flow (used when registering with an email that was
   * pre-onboarded by an admin). Step 1: request a 6-digit OTP be sent to
   * the admin-created owner's email.
   */
  async claimProfileSendOTP(email: string) {
    const response = await this.apiV1.post('/auth/claim-profile/send-otp', { email })
    return response.data
  }

  /** Step 2: verify the OTP and set the owner's new password in one call. */
  async claimProfileComplete(email: string, otp: string, password: string, acceptedTerms: boolean) {
    const response = await this.apiV1.post('/auth/claim-profile/complete', {
      email,
      otp,
      password,
      acceptedTerms,
    })
    return response.data
  }

  // Utility methods
  async uploadFile(file: File, endpoint: string, onProgress?: (progress: number) => void) {
    const formData = new FormData()
    formData.append('file', file)

    const response = await this.api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      },
    })
    return response.data
  }

  // Message endpoints (use V1 API - /api/messages, not /api/v2/messages)
  async sendMessage(data: { schoolId: string; content: string; receiverId?: string; bookingId?: string }) {
    const response = await this.apiV1.post('/messages', data)
    return response.data
  }

  async getMessages(params?: any) {
    const response = await this.apiV1.get('/messages', { params })
    return response.data
  }

  async getConversations(params?: any) {
    const response = await this.apiV1.get('/messages/conversations', { params })
    return response.data
  }

  async markMessagesAsRead(data: { messageIds: string[] }) {
    const response = await this.apiV1.patch('/messages/read', data)
    return response.data
  }

  async getUnreadCount() {
    const response = await this.apiV1.get('/messages/unread-count')
    return response.data
  }

  // Generic CRUD methods
  async get(endpoint: string, params?: any) {
    const response = await this.api.get(endpoint, { params })
    return response.data
  }

  async post(endpoint: string, data?: any, config?: any) {
    const response = await this.api.post(endpoint, data, config)
    return response.data
  }

  async put(endpoint: string, data?: any, config?: any) {
    const response = await this.api.put(endpoint, data, config)
    return response.data
  }

  async patch(endpoint: string, data?: any, config?: any) {
    const response = await this.api.patch(endpoint, data, config)
    return response.data
  }

  async delete(endpoint: string, config?: any) {
    const response = await this.api.delete(endpoint, config)
    return response.data
  }
}

export const apiService = new ApiService()
export default apiService
